import React, { useCallback, useContext, useMemo } from 'react';
import { MenuItem } from '~/types/Menu';
import { formatPrice } from '~/utils/currency';
import useOpenCartPanel from '~/App/CartPanel/hooks/useOpenCartPanel';
import {
  CartActionContext,
  CartStateContext,
} from '~/App/Layout/providers/CartProvider';
import { CartItem } from '~/types/Cart';
import { MenuAvailability } from '@restaurant/api';
import { MenuAvailabilityType } from '@restaurant/types';
import cs from 'clsx';

const ItemCard = ({
  item,
  availability,
}: {
  item: MenuItem;
  availability: MenuAvailability | undefined;
}) => {
  const cart = useContext(CartStateContext);
  const { addToCart } = useContext(CartActionContext);
  const openCartPanel = useOpenCartPanel();

  const { isDisabled, stock } = useMemo(() => {
    if (!availability)
      return {
        isDisabled: false,
        stock: null,
      };

    const { availabilityType, stockCounter } = availability;

    const stock = (() => {
      if (
        availabilityType !== MenuAvailabilityType.CountStock ||
        stockCounter?.current == null
      )
        return null;

      const inCartCount = cart.reduce((acc, cur) => {
        if (cur.menuId === item.id) {
          acc += cur.quantity;
        }

        cur.addOns.forEach((addOn) => {
          if (addOn.menuId === item.id) {
            acc += addOn.quantity;
          }
        });

        return acc;
      }, 0);
      return Math.max(stockCounter.current - inCartCount, 0);
    })();

    const isDisabled =
      availabilityType === MenuAvailabilityType.ToggleOff || stock === 0;

    return { isDisabled, stock };
  }, [availability, cart, item.id]);

  const onClick = useCallback(() => {
    if (isDisabled) return;

    openCartPanel({
      item,
      onSubmit: (cartItem: CartItem) => {
        addToCart(cartItem);
        // message.success('Pesanan ditambahkan');
      },
    });
  }, [item, isDisabled]);

  return (
    <div
      className={cs(
        'w-full rounded-lg border border-solid border-black overflow-hidden',
        isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
      )}
      onClick={onClick}
    >
      <div className="w-full relative">
        <div className="text-2xl w-full h-[100px] flex items-center justify-center text-center py-1">
          {item.name}
        </div>
        {/* {isSelected ? (
          <div className="absolute inset-0 flex items-center justify-center pb-2 bg-black/50 text-white text-lg font-semibold">
            <span>{i18n.t('menu_card_selected_quantity', { quantity })}</span>
          </div>
        ) : null} */}
        <div className="flex justify-center bg-neutral-400 text-white font-medium py-1 text-lg">
          <span>{formatPrice(item.price)}</span>
        </div>
        {isDisabled && (
          <div className="absolute inset-0 bg-black z-5 bg-opacity-60" />
        )}
        {stock != null && (
          <div className="absolute top-0 right-0 rounded-bl-lg rounded-tr-lg px-2 bg-red-200 text-base">
            {`Sisa ${stock}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemCard;
